





















import Enumerable from "linq";
import Moment from "moment";
import { SortOrder } from "element-ui/types/table";
import { Component, Mixins, Model, Prop, Watch, Emit } from "vue-property-decorator";
import { DomainRegistry } from "../../../domain";
import {
  DocumentSummary,
  DocumentTypeKinds,
  DocumentListSortItemKinds,
  DocumentListPresetSortCondition,
  DocumentListPresetSortConditionItem,
} from "../../../domain/document";
import { Comparator } from "../../../domain/system";
import OptionDropDownSelector from "../../system/selectors/OptionDropDownSelector.vue";
import { StandardOption } from "../../system/selectors/mixins";
import { ISortParameter } from "../../system";
import ListMultiSortConditionDialog, { IListMultiSortConditionDialogParameter, IListMultiSortConditionDialogResult } from "../dialogs/ListMultiSortConditionDialog.vue";

interface IDocumentsColumn {
  name: string;
  kind: DocumentListSortItemKinds;
  toValue: ToValueType;
  isPatientColumn?: boolean;
}

interface IDocumentSortColumn {
  key: string;
  value: IDocumentsColumn;
}

type ToValueType = (s: DocumentSummary) => number | boolean | string | Date;

@Component({
  components: {
    OptionDropDownSelector,
    ListMultiSortConditionDialog,
  }
})
export default class DocumentsSort extends Mixins() {
  $refs!: {
    multiSortConditionDialog: ListMultiSortConditionDialog<DocumentListSortItemKinds>;
  };

  @Model("input")
  value: DocumentSummary[];

  @Prop()
  documents: DocumentSummary[];

  @Prop()
  sortParameter: ISortParameter;

  @Prop()
  shouldShowPatientColumns: boolean;

  @Prop()
  shouldShowListOptionsOnly: boolean;

  private get _defaultColumns(): IDocumentSortColumn[] {
    return [
      { key: "contentDateTime", value: { name: "文書日時", kind: DocumentListSortItemKinds.ContentDateTime, toValue: s => s.contentDateTime }},
      { key: "patientNo", value: { name: "患者番号", kind: DocumentListSortItemKinds.PatientNo, toValue: s => s.patientNo, isPatientColumn: true }},
      { key: "patientName", value: { name: "患者名", kind: DocumentListSortItemKinds.PatientName, toValue: s => s.patientName, isPatientColumn: true }},
      { key: "department", value: { name: this.$wording.common.department, kind: DocumentListSortItemKinds.Department, toValue: s => s.department && s.department.name ? s.department.name : "" }},
      { key: "documentName", value: { name: this.$wording.format.attributes.name + "名", kind: DocumentListSortItemKinds.DocumentName, toValue: s => s.documentName }},
      { key: "companyName", value: { name: this.$wording.format.attributes.company + "名", kind: DocumentListSortItemKinds.CompanyName, toValue: s => s.companyName ? s.companyName : "" }},
      { key: "createStaffName", value: { name: "作成者", kind: DocumentListSortItemKinds.CreateStaffName, toValue: s => s.createStaffName }},
      { key: "orderUserName", value: { name: "指示者", kind: DocumentListSortItemKinds.OrderUserName, toValue: s => s.orderUserName }},
      { key: "attendingUserName", value: { name: "指導者", kind: DocumentListSortItemKinds.AttendingUserName, toValue: s => s.attendingUserName }},
      { key: "createdDateTime", value: { name: "更新日時", kind: DocumentListSortItemKinds.CreatedDateTime, toValue: s => s.createdDateTime }},
      { key: "published", value: { name: "発行状態", kind: DocumentListSortItemKinds.Published, toValue: s => s.published }},
      { key: "publishedCount", value: { name: "印刷履歴件数", kind: DocumentListSortItemKinds.PublishedCount, toValue: s => s.publishedCount }},
      { key: "describedMethod", value: { name: "記載方法", kind: DocumentListSortItemKinds.DescribedMethod, toValue: s => DocumentsSort.toDescrivedMethodWeight(s) }},
      { key: "note", value: { name: this.$wording.document.note, kind: DocumentListSortItemKinds.Note, toValue: s => s.note }},
      { key: "edition", value: { name: "版数", kind: DocumentListSortItemKinds.Edition, toValue: s => s.edition }},
      { key: "id", value: { name: "文書ID", kind: DocumentListSortItemKinds.Id, toValue: s => s.id }},
    ];
  }

  private get _multiConditionColum(): IDocumentSortColumn {
    return { key: "multiCondition", value: { name: "複数条件", kind: DocumentListSortItemKinds.MultiCondition, toValue: null }};
  }

  private get _presetSortConditionColumns(): IDocumentSortColumn[] {
    const presetColumns = Enumerable.from(this.presetSortConditions ? this.presetSortConditions : [])
      .select((p, i) => this.toPresetSortConditionColumns(i, p.label, null, false))
      .toArray();
    return [this._multiConditionColum].concat(presetColumns);
  }

  private get _columns(): IDocumentSortColumn[] {
    return this._presetSortConditionColumns.concat(this._defaultColumns);
  }

  get selectedSingleSortCondition(): string {
    if (!this.sortType || (this.selectedSortConditionItems && this.selectedSortConditionItems.length > 0)) { return ""; }
    const column = this.getColumnByKind(this.sortType.kind);
    return column.name + (this.orderType === "ascending" ? "(昇順)" : "(降順)");
  }

  get selectedSortConditions(): string {
    const sortConditionItems = Enumerable.from(this.selectedSortConditionItems)
      .select((s) => {
        const column = this.getColumnByKind(s.sortType);
        return column.name + (s.orderType === "ascending" ? "(昇順)" : "(降順)");
      })
      .toArray();
    return (sortConditionItems && sortConditionItems.length > 0)
      ? sortConditionItems.join(" ＞ ")
      : this.selectedSingleSortCondition;
  }

  private toDocumentSortColumn(key: string, name: string, kind: DocumentListSortItemKinds, toValue: ToValueType, isPatientColumn: boolean): IDocumentSortColumn {
    return {
      key: key,
      value: { name: name, kind: kind, toValue: toValue, isPatientColumn: isPatientColumn }
    };
  }

  private toPresetSortConditionColumns(index: number, name: string, toValue: ToValueType, isPatientColumn: boolean): IDocumentSortColumn {
    const itemKind = DocumentListSortItemKinds.PresetMin + index;
    return this.toDocumentSortColumn(itemKind.toString(), name, itemKind, toValue, isPatientColumn);
  }

  getColumnKeyByKind(kind: DocumentListSortItemKinds): string {
    return Enumerable.from(this._columns)
      .where(c => c.value.kind === kind)
      .select(c => c.key)
      .firstOrDefault();
  }

  getColumnByKind(kind: DocumentListSortItemKinds): IDocumentsColumn {
    return Enumerable.from(this._columns)
      .where(c => c.value.kind === kind)
      .select(c => c.value)
      .firstOrDefault();
  }

  getColumnByKey(key: string): IDocumentsColumn {
    return Enumerable.from(this._columns)
      .where(c => c.key === key)
      .select(c => c.value)
      .firstOrDefault();
  }

  get sortTypeOptions(): StandardOption<IDocumentsColumn>[] {
    return Enumerable.from(this._columns)
      .where(c => this.shouldShowColumn(c))
      .select(c => c.value)
      .select(c => new StandardOption<IDocumentsColumn>(c.name, c))
      .toArray();
  }

  private shouldShowColumn(column: IDocumentSortColumn): boolean {
    const displayColumnNames = this.$configuration.behavior.document.list.selector.style.list.columns;
    return ((this.shouldShowPatientColumns || !column.value.isPatientColumn)
      && (!this.shouldShowListOptionsOnly || displayColumnNames.length < 1 || displayColumnNames.includes(column.key) || column.value.kind >= DocumentListSortItemKinds.MultiCondition));
  }

  get defaultSortType(): IDocumentsColumn {
    return this.shouldShowPatientColumns
      ? this.getColumnByKind(DocumentListSortItemKinds.CreatedDateTime)
      : this.getColumnByKind(DocumentListSortItemKinds.ContentDateTime);
  }

  get defaultOrderType(): SortOrder {
    return "descending";
  }

  get isSortSettingEnable(): boolean {
    return this.$configuration.behavior.document.list.sort.default.isEnable;
  }

  get defaultColumnKey(): string {
    return this.$configuration.behavior.document.list.sort.default.columnKey;
  }

  get settingSortType(): IDocumentsColumn {
    return this.getColumnByKey(this.defaultColumnKey);
  }

  get settingOrderType(): SortOrder {
    return this.$configuration.behavior.document.list.sort.default.isDescending
      ? "descending"
      : "ascending";
  }

  get isSettingSortTypeEnable(): boolean {
    return this.settingSortType && this.sortTypeOptions.some(o => o.label === this.settingSortType.name && o.value === this.settingSortType);
  }

  get isMultiCondition(): boolean {
    return this.sortType
      && this.sortType.kind === DocumentListSortItemKinds.MultiCondition;
  }

  get isPresetCondition(): boolean {
    return this.sortType
      && this.sortType.kind === Math.min(Math.max(this.sortType.kind, DocumentListSortItemKinds.PresetMin), DocumentListSortItemKinds.PresetMax);
  }

  get isDimensionalSortCondition(): boolean {
    return this.isMultiCondition || this.isPresetCondition;
  }

  get isCommonPresetSortCondition(): boolean {
    return this.sortType && this.presetSortCondition && this.presetSortCondition.isCommon;
  }

  get presetSortCondition(): DocumentListPresetSortCondition {
    return this.presetSortConditions[this.presetSortConditionIndex];
  }

  get presetSortConditionIndex(): number {
    return this.sortType.kind - DocumentListSortItemKinds.PresetMin;
  }

  @Watch("shouldShowPatientColumns")
  onShouldShowPatientColumnsUpdated(newValue: boolean, oldValue: boolean): void {
    this.initSortType();
  }

  @Watch("documents")
  onDocumentsUpdated(newValue: DocumentSummary[], oldValue: DocumentSummary[]): void {
    this.execSort();
  }

  @Watch("sortParameter")
  onSortParameterUpdated(newValue: ISortParameter, oldValue: ISortParameter): void {
    this.sortType = this.getColumnByKey(newValue.property);
    this.orderType = newValue.order;
  }

  @Watch("sortType")
  async onSortTypeUpdated(newValue: IDocumentsColumn, oldValue: IDocumentsColumn): Promise<void> {
    if (!oldValue || newValue.name !== oldValue.name) {
      if (newValue.kind === DocumentListSortItemKinds.MultiCondition) {
        if (!await this.showMultiSortConditionDialog(this.toMultiConditionSortDialogParam(oldValue))) {
          this.sortType = oldValue;
        }
      } else if (newValue.kind === Math.min(Math.max(newValue.kind, DocumentListSortItemKinds.PresetMin), DocumentListSortItemKinds.PresetMax)) {
        this.selectedSortConditionItems = this.presetSortCondition.items;
        this.execSort();
      } else {
        this.selectedSortConditionItems = [];
        this.execSort();
        this.updateSortParameter();
      }
    }
  }

  @Watch("orderType")
  onOrderTypeUpdated(newValue: SortOrder, oldValue: SortOrder): void {
    if (newValue !== oldValue) {
      this.execSort();
      this.updateSortParameter();
    }
  }

  @Emit()
  input(value: DocumentSummary[]): void {}

  @Emit()
  dimensionalSortStarted(): void {}

  data(): any {
    return {
      sortType: null,
      orderType: null,
      presetSortConditions: [],
      selectedSortConditionItems: [],
      isLoading: false,
    };
  }
  sortType: IDocumentsColumn;
  orderType: SortOrder;
  presetSortConditions: DocumentListPresetSortCondition[];
  selectedSortConditionItems: DocumentListPresetSortConditionItem[];
  isLoading: boolean;

  async mounted(): Promise<void> {
    this.isLoading = true;
    this.initSortType();
    await this.initializePresetSortCondition();
    this.isLoading = false;
  }

  toDateString(source: Date): string {
    return Moment(source).format("YY/MM/DD");
  }

  private static isWorkflowDocument(documentType: DocumentTypeKinds): boolean {
    return documentType === DocumentTypeKinds.Workflow;
  }

  initSortType(): void {
    if (this.isSortSettingEnable && this.isSettingSortTypeEnable) { 
      this.sortType = this.settingSortType;
      this.orderType = this.settingOrderType;
    } else {
      this.sortType = this.defaultSortType;
      this.orderType = this.defaultOrderType;
    }
  }

  private async initializePresetSortCondition(): Promise<void> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      const result = await repository.getPresetSortCondition();
      this.presetSortConditions = result;
      const index = this.presetSortConditions.findIndex(v => v.isDefault);
      if (index !== -1) {
        this.setPresetSortType(index);
      }
    } catch (e) {
      this.$error("ソート条件プリセット取得エラー", "ソート条件のプリセット取得に失敗しました。", e);
    }
  }

  private setPresetSortType(index: number): void {
    this.sortType = this.getColumnByKind(DocumentListSortItemKinds.PresetMin + index);
  }

  execSort(): void {
    if (!this.documents || this.documents.length === 0 || !this.sortType || !this.orderType) {
      this.input([]);
      return;
    }

    const list = this.documents.slice();  // 複製して処理する
    if (this.isDimensionalSortCondition) {
      this.execDimensionalSort(list);
    } else {
      this.sortList(list, this.sortType.toValue, this.orderType);
    }
    this.input(list);
  }

  private compare(a: any, b: any, orderType: SortOrder): number {
    return Comparator.compare(a, b, orderType === "ascending");
  }

  sortList(list: DocumentSummary[], toValue: ToValueType, order: SortOrder): void {
    list.sort((a, b): number => { 
      return this.compare(toValue(a), toValue(b), order);
    });
  }

  execDimensionalSort(list: DocumentSummary[]) {
    const items = this.selectedSortConditionItems.filter(v => v.sortType !== null && v.orderType !== null);
    console.log("dimensional sort " + items
      .map(v => DocumentListSortItemKinds[v.sortType] + " " + this.getOrderString(v.orderType))
      .join(" - "));

    // リスト表示で列のソートマークが残ってしまうため、複数条件ソート開始を通知してtableのソートをクリアしている
    this.dimensionalSortStarted();
    list.sort((a, b) => {
      for (const item of items) {
        const result = this.compare(this.getColumnByKind(item.sortType).toValue(a), this.getColumnByKind(item.sortType).toValue(b), item.orderType);
        if (result !== 0) {
          return result;
        }
      }
    });
  }

  private getOrderString(orderType: SortOrder): string {
    return (orderType === "ascending" ? "asc" : "desc");
  }

  private static toDescrivedMethodWeight(source: DocumentSummary): number {
    // 並び順
    // 直接記載
    // --> 一時保存
    // --> 通常
    // --> 削除済み

    // ワークフロー
    // --> 一時保存
    // --> 通常
    // --> 削除済み

    return (this.isWorkflowDocument(source.documentType) ? 100 : 0)
      + (source.isDeleted ? 50 : 0)
      + ((!source.isTemporary && !source.isDeleted) ? 10 : 0);
  }

  private updateSortParameter(): void {
    // keyを_columnsに定義した方が良い？
    const columnKey = this.getColumnKeyByKind(this.sortType.kind);
    const parameter: ISortParameter = {
      property: columnKey,
      order: this.orderType,
    };
    this.$emit("update:sortParameter", parameter);
  }

  private toMultiConditionSortDialogParam(sortType: IDocumentsColumn): IListMultiSortConditionDialogParameter<DocumentListSortItemKinds> {
    return {
      isEdit: false,
      presetName: "",
      sortConditionItems: this.selectedSortConditionItems.length > 0
        ? this.selectedSortConditionItems
        : [{ sortType: sortType.kind, orderType: this.orderType }],
      sortTypeOptions: Enumerable.from(this._defaultColumns).select(s => new StandardOption<DocumentListSortItemKinds>(s.value.name, s.value.kind)).toArray(),
      defaultSort: false,
      isSortButtonEnable: true,
    };
  }

  private toPresetSortDialogParam(): IListMultiSortConditionDialogParameter<DocumentListSortItemKinds> {
    return {
      isEdit: true,
      presetName: this.presetSortCondition.label,
      sortConditionItems: this.presetSortCondition.items,
      sortTypeOptions: Enumerable.from(this._defaultColumns).select(s => new StandardOption<DocumentListSortItemKinds>(s.value.name, s.value.kind)).toArray(),
      defaultSort: this.presetSortCondition.isDefault,
      isSortButtonEnable: true,
    };
  }

  private async showMultiSortConditionDialog(param: IListMultiSortConditionDialogParameter<DocumentListSortItemKinds>): Promise<boolean> {
    const dialogResult = await this.$refs.multiSortConditionDialog.show(param);
    await this.execSortDialogResult(dialogResult);
    return dialogResult.result !== "cancel";
  }

  private async execSortDialogResult(result: IListMultiSortConditionDialogResult): Promise<void> {
    switch (result.result) {
      case "sort":
        this.selectedSortConditionItems = result.sortConditionItems;
        this.execSort();
        break;
      case "add":
        const model = new DocumentListPresetSortCondition();
        model.label = result.presetName;
        model.items = result.sortConditionItems;
        model.isDefault = result.defaultSort;
        model.isCommon = false;
        if (result.defaultSort) {
          this.setPresetSortConditionsNotDefault();
        }
        const commonConditionIndex = this.presetSortConditions.findIndex(p => p.isCommon);
        if (commonConditionIndex === -1) {
          this.presetSortConditions.push(model);
          this.setPresetSortType(this.presetSortConditions.length - 1);
        } else {
          this.presetSortConditions.splice(commonConditionIndex, 0, model);
          this.setPresetSortType(commonConditionIndex);
        }
        this.registerPresetSortCondition(false);
        break;
      case "rename":
        this.presetSortCondition.label = result.presetName;
        this.registerPresetSortCondition(false);
        break;
      case "remove":
        this.presetSortConditions.splice(this.presetSortConditionIndex, 1);
        this.registerPresetSortCondition(true);
        this.initSortType();
        break;
      case "update":
        this.presetSortCondition.label = result.presetName;
        this.presetSortCondition.items = result.sortConditionItems;
        if (result.defaultSort) {
          this.setPresetSortConditionsNotDefault();
        }
        this.presetSortCondition.isDefault = result.defaultSort;
        this.registerPresetSortCondition(false);
        this.selectedSortConditionItems = result.sortConditionItems;
        this.execSort();
        break;
    }
  }

  private setPresetSortConditionsNotDefault(): void {
    this.presetSortConditions.forEach(v => {
      if (!v.isCommon) {
        v.isDefault = false;
      }
    });
  }

  private async registerPresetSortCondition(isRemove: boolean): Promise<void> {
    const action = isRemove ? "削除" : "登録";
    try {
      this.isLoading = true;
      const repository = DomainRegistry.document.getDocumentRepository();
      await repository.registerPresetSortCondition(this.presetSortConditions.filter(p => !p.isCommon));
      this.$success("ソート条件プリセット" + action + "完了", "ソート条件のプリセットを" + action + "しました。");
    } catch (e) {
      this.$error("ソート条件プリセット" + action + "エラー", "ソート条件のプリセット" + action + "に失敗しました。", e);
    } finally {
      this.isLoading = false;
    }
  }

  async editDimensionalSortCondition(): Promise<void> {
    const param = this.isMultiCondition
      ? this.toMultiConditionSortDialogParam(this.sortType)
      : this.toPresetSortDialogParam();
    await this.showMultiSortConditionDialog(param);
  }

  areKindEqual(source: IDocumentsColumn, destination: IDocumentsColumn): boolean {
    return source
      && destination
      && source.kind === destination.kind;
  }
}
